import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

export function VideoPlayer({ videoSrc, captionSrc }) {
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [timeText, setTimeText] = useState('5시간');
  const animationFrameId = useRef(null);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      animationFrameId.current = requestAnimationFrame(() => {
        const { currentTime, duration } = videoRef.current;
        const newProgress = (currentTime / duration) * 100;
        if (newProgress >= 100) {
          setProgress(100);
          setTimeText('현재시간');
        } else {
          setProgress(newProgress);
          updateTimeText(newProgress);
        }
      });
    }
  };

  const updateTimeText = (progress) => {
    const totalMinutes = 300; // 5시간 = 300분
    const elapsedMinutes = (progress / 100) * totalMinutes;
    const remainingMinutes = totalMinutes - elapsedMinutes;
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = Math.floor(remainingMinutes % 60);
    const remainingText =
      remainingMinutes === 0 ? '현재시간' : minutes === 0 ? `${hours}시간 전` : `${hours}시간 ${minutes}분 전`;
    setTimeText(remainingText);
  };

  const updateProgress = (clientX) => {
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = clientX - rect.left;
    const newProgress = (clickPosition / rect.width) * 100;
    if (newProgress >= 100) {
      setProgress(100);
      setTimeText('현재시간');
    } else {
      setProgress(newProgress);
      if (videoRef.current) {
        videoRef.current.currentTime = (newProgress / 100) * duration;
        updateTimeText(newProgress);
      }
    }
  };

  const handleTouchStart = (event) => {
    updateProgress(event.touches[0].clientX);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (event) => {
    updateProgress(event.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };

  useEffect(() => {
    const handleLoadedMetadata = () => {
      if (videoRef.current) {
        setDuration(videoRef.current.duration);
        updateTimeText(0);
      }
    };

    const videoElement = videoRef.current;
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [duration]);

  return (
    <VideoContainer>
      <VideoBox>
        <video ref={videoRef} width="100%" height="100%" controls>
          <source src={videoSrc} type="video/mp4" />
          <track src={captionSrc} kind="captions" srcLang="en" label="English" />
          브라우저가 비디오 태그를 지원하지 않습니다.
        </video>
      </VideoBox>
      <ProgressWrap>
        <ProgressBox onTouchStart={handleTouchStart}>
          <ProgressBar ref={progressBarRef}>
            <ProgressFill style={{ width: `${progress}%` }} />
            <ProgressThumb style={{ left: `${progress}%` }} />
          </ProgressBar>
        </ProgressBox>
        <ProgressLabels>
          <span>{timeText}</span>
          <span>현재시간</span>
        </ProgressLabels>
      </ProgressWrap>
    </VideoContainer>
  );
}

const VideoContainer = styled.div`
  padding: 16px;
`;

const VideoBox = styled.div`
  border-radius: 8px;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
`;

const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
`;

const ProgressBox = styled.div`
  width: 100%;
  padding: 16px 11px;
  position: relative;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.gray300};
  border-radius: 2px;
  cursor: pointer;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 2px;
  transition: width 150ms;
`;

const ProgressThumb = styled.div`
  width: 14px;
  height: 14px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: left 150ms;
`;

const ProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 4px;
  font-size: 12px;
  padding: 0 8px;
`;
